/* ════════════════════════════════════════════════════════════════
   FORMATTERS — Formatação de dados para exibição

   Responsabilidade: converter valores brutos (ex: strings ISO 8601)
   em texto legível para o usuário em tabelas, cards e detalhes.

   Não usar para mascaramento de inputs → ver masks.ts
   Não usar para validação de regras de negócio → ver validators.ts
════════════════════════════════════════════════════════════════ */

/** Formata data ISO para dd/MM/yyyy HH:mm (ou "—" se vazio).
 *  Aceita alta precisão de segundos fracionários ("2026-04-30T19:15:06.3594677").
 *  Opera sobre a string diretamente — sem conversão de fuso horário. */
export const fmtDate = (s: string | null | undefined): string => {
  if (!s) return "—";
  const normalized = s.replace("T", " ");
  const [date, time] = normalized.split(" ");
  const [y, m, d] = date.split("-");
  const hhmm = time ? time.substring(0, 5) : "";
  return hhmm ? `${d}/${m}/${y} ${hhmm}` : `${d}/${m}/${y}`;
};

/** Formata data ISO para dd/MM/yyyy (ou "—" se vazio).
 *  Descarta qualquer componente de hora sem conversão de fuso. */
export const fmtDateOnly = (s: string | null | undefined): string => {
  if (!s) return "—";
  const datePart = s.includes("T") ? s.split("T")[0] : s.split(" ")[0];
  const [y, m, d] = datePart.split("-");
  return `${d}/${m}/${y}`;
};

/**
 * Serializa um valor numérico para string pt-BR (vírgula como separador decimal)
 * com arredondamento, pronto para envio via FormData ao back-end.
 *
 * - casas = 2 para campos monetários/percentuais (DECIMAL(18,2))
 * - casas = 3 para PesoBruto, PesoLiquido e FatorConversao (DECIMAL(18,3))
 *
 * Retorna o valor inalterado se null, undefined ou string vazia.
 */
/** Formata valor numérico como moeda BRL (R$ 1.234,56), ou "—" se null/undefined.
 *  Preserva 0 → "R$ 0,00"; só ausência de valor vira "—" (mesma convenção de fmtDate). */
export const fmtCurrency = (v: number | null | undefined): string =>
  v == null ? "—" : v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/** Formata valor numérico como percentual pt-BR (ex: 5 → "5%", 1.5 → "1,5%"), ou "—" se null/undefined.
 *  Sem casas obrigatórias; até 2 casas decimais. */
export const fmtPercent = (v: number | null | undefined): string =>
  v == null
    ? "—"
    : `${v.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;

/** Formata quantidade decimal em pt-BR sem casas irrelevantes.
 *  3 → "3" · 2.5 → "2,5" · "3.0000" → "3" (tolera decimal serializado como string). */
export const fmtQuantidade = (v: number | string | null | undefined): string => {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n)
    ? n.toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      })
    : "0";
};

export function formatDecimalPtBR(
  value: number | string | null | undefined,
  casas: 2 | 3 = 2
): string {
  if (value === null || value === undefined || value === "")
    return String(value ?? "");
  return parseFloat(String(value)).toFixed(casas).replace(".", ",");
}
